/**
 * Creates a JWT token with the provided payload
 * @param payload - The payload to include in the JWT
 * @returns A JWT token string
 */
export function createJWT(payload: Record<string, unknown>): string {
  // Note: In a production environment, you should sign the token with a secret key
  // This is a simplified version that doesn't include a signature
  const header = {
    alg: 'none',
    typ: 'JWT'
  };

  const encodedHeader = btoa(JSON.stringify(header)).replace(/=+$/, '');
  const encodedPayload = btoa(JSON.stringify(payload)).replace(/=+$/, '');
  
  // In a real implementation, you would sign the token here
  // For now, we'll just return the header and payload without a signature
  return `${encodedHeader}.${encodedPayload}.`;
}

/**
 * Decodes a JWT token without verification
 * @param token - The JWT token to decode
 * @returns The decoded token payload or null if invalid
 */
export function decodeJWT<T = unknown>(token: string): T | null {
  if (!token) return null;
  
  try {
    // Split the token into its parts
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }
    
    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    
    // Parse and return the payload
    return JSON.parse(decoded) as T;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Type guard to check if an object has a specific property
 */
function hasProperty<T extends object, K extends string>(
  obj: T,
  prop: K
): obj is T & Record<K, unknown> {
  return prop in obj;
}

/**
 * Safely gets a value from a JWT payload
 * @param token - The JWT token
 * @param key - The key to get from the payload
 * @returns The value or undefined if not found
 */
export function getJWTValue<T = unknown>(
  token: string,
  key: string
): T | undefined {
  const payload = decodeJWT<Record<string, unknown>>(token);
  return payload && hasProperty(payload, key) ? (payload[key] as T) : undefined;
}
